import ReactDOMServer from 'react-dom/server';
import { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import MaskedInput from 'react-text-mask';
import { DrawType } from '@apply/constants';
import CloseIcon from '@material-ui/icons/Close';
import { EsignModal } from '../../esign';
import { BrokerClientApi, DrawApi } from '@apply/api';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

export interface IRequestDrawModalProps {
    drawBudgetId: number;
    isOutOfBalance: boolean;
    isOutOfBalanceWaived: boolean;
    currentAccountNumber: string;
    percentFunded: number;
    open: boolean;
    applicationInsights: ApplicationInsights;
    borrowerName: string;
    submitDrawRequest: ({
        drawBudgetId,
        drawType,
        fontId,
        ipAddress,
    }: {
        drawBudgetId: number;
        drawType: DrawType;
        fontId: number | undefined;
        ipAddress: string | undefined;
    }) => void;
    handleCloseEvent: () => void;
    isAdmin?: boolean;
}

export const RequestDrawModal = ({
    drawBudgetId,
    isOutOfBalance,
    isOutOfBalanceWaived,
    open,
    applicationInsights,
    borrowerName,
    percentFunded,
    submitDrawRequest,
    handleCloseEvent,
    currentAccountNumber,
    isAdmin = false,
}: IRequestDrawModalProps): JSX.Element => {
    const styles = {
        alertSection: {
            color: '#770000',
            width: '380px',
            margin: 'auto',
            fontSize: '14px',
            paddingTop: '10px',
            display: 'flex',
        },
        alertAsterisk: {
            width: 'auto',
        },
        alertContent: {
            marginLeft: '5px',
        },
    };
    const manualNotAvailableText = '* Manual not available. Prior draw was over funded.';

    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    const [isAutomatic, set_isAutomatic] = useState<boolean>(true);
    const [automaticButtonClasses, set_automaticButtonClasses] = useState<string>('button');
    const [enteredAccountNumber, set_enteredAccountNumber] = useState<string>();

    const [isManual, set_isManual] = useState<boolean>(false);
    const [manualButtonClasses, set_manualButtonClasses] = useState<string>('button drawButtonOff');

    const [isSubmitted, set_isSubmitted] = useState<boolean>(false);
    const [isEsignOpen, set_isEsignOpen] = useState<boolean>(false);
    const [ipAddress, set_ipAddress] = useState<string>();

    const onAutomaticButtonClick = () => {
        set_isAutomatic(true);
        set_automaticButtonClasses('button');

        set_isManual(false);
        set_manualButtonClasses('button drawButtonOff');
    };

    const onManualButtonClick = () => {
        set_isAutomatic(false);
        set_automaticButtonClasses('button drawButtonOff');

        set_isManual(true);
        set_manualButtonClasses('button');
    };

    const handleSubmit = () => {
        if (isAutomatic && !isManual) {
            if (isAdmin) {
                submitDrawRequest({
                    drawBudgetId,
                    drawType: DrawType.Automatic,
                    fontId: undefined,
                    ipAddress: undefined,
                });
                set_isSubmitted(true);
            } else {
                brokerClientApi.getIpAddress().then((response) => {
                    if (response.statusCode !== 200) return;

                    set_ipAddress(response.result);
                    set_isEsignOpen(true);
                });
            }
        }

        if (!isAutomatic && isManual) {
            if (currentAccountNumber?.toString() !== enteredAccountNumber) {
                drawApi.drawBudgetBankAccountLast4DigitsOn(drawBudgetId, enteredAccountNumber);
            }

            submitDrawRequest({
                drawBudgetId,
                drawType: DrawType.Manual,
                fontId: undefined,
                ipAddress: undefined,
            });
            set_isSubmitted(true);
        }

        if ((isAutomatic && isManual) || (!isAutomatic && !isManual)) {
            throw new Error('Invalid state for request draw modal.');
        }
    };

    const handleClose = () => {
        handleCloseEvent();

        // Reset modal for another draw
        set_isSubmitted(false);
    };

    const onConfirmAgreement = (font) => {
        if (currentAccountNumber?.toString() !== enteredAccountNumber) {
            drawApi.drawBudgetBankAccountLast4DigitsOn(drawBudgetId, enteredAccountNumber);
        }

        submitDrawRequest({
            drawBudgetId,
            drawType: DrawType.Automatic,
            fontId: font.id,
            ipAddress: ipAddress,
        });

        set_isSubmitted(true);
        set_isEsignOpen(false);
    };

    const onRejectAgreement = () => {
        set_isEsignOpen(false);
    };

    const automaticSubmittedDialog = (
        <>
            <DialogContent>
                <div className='drawWrapper'>
                    <div className='drawFlexRow'>
                        <p className='drawThankYouMessage'>
                            Thank you! We will endeavor to fund
                            <br />
                            within three business days.
                        </p>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <div className='row'>
                    <button
                        type='button'
                        className='button primary'
                        onClick={handleClose}
                    >
                        Close
                    </button>
                </div>
            </DialogActions>
        </>
    );

    const manualSubmittedDialog = (
        <>
            <DialogContent>
                <div className='drawWrapper'>
                    <div className='drawFlexRow'>
                        <p className='drawThankYouMessage'>Thank you! Please enter your budget information on the Draw Form.</p>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <div className='row'>
                    <button
                        type='button'
                        className='button primary'
                        onClick={() => (window.location.href = `/draw/${drawBudgetId}`)}
                    >
                        Go to Draw Form
                    </button>
                </div>
            </DialogActions>
        </>
    );

    const submittedDialog = isAutomatic && !isManual ? automaticSubmittedDialog : manualSubmittedDialog;

    const canSubmit = () => {
        if (isAdmin) return true;

        if (enteredAccountNumber?.length === 4) {
            return true;
        } else {
            return false;
        }
    };

    const bankAccountChanged = (enteredAccount) => {
        const cleanedAccount = ('' + enteredAccount).replace(/\D/g, '');

        set_enteredAccountNumber(cleanedAccount);
    };

    const lateStageDrawThreshold = () => {
        if (percentFunded > 80) {
            return true;
        }
        return false;
    };

    const drawSelectorDialog = (
        <>
            <DialogContent>
                <div className='drawWrapper'>
                    <div className='drawButtonRow'>
                        <button
                            type='button'
                            className={automaticButtonClasses}
                            onClick={onAutomaticButtonClick}
                        >
                            Automatic
                        </button>
                        <button
                            type='button'
                            className={manualButtonClasses}
                            onClick={onManualButtonClick}
                            disabled={isOutOfBalance && !isOutOfBalanceWaived}
                            title={isOutOfBalance && !isOutOfBalanceWaived ? manualNotAvailableText : ''}
                        >
                            Manual {lateStageDrawThreshold() && <span color='red'> * </span>}
                        </button>
                    </div>
                    {!isAdmin && (
                        <>
                            <div className='drawInputRow drawFlexRow'>
                                <p style={{ paddingRight: '5px' }}>Please enter last 4 digits of bank account:</p>
                                <MaskedInput
                                    mask={[/\d/, /\d/, /\d/, /\d/]}
                                    guide={true}
                                    type='text'
                                    onChange={(event) => bankAccountChanged(event.target.value)}
                                />
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
            {
                // Out of Balance alert
                isOutOfBalance && !isOutOfBalanceWaived && (
                    <div style={styles.alertSection}>
                        <div style={styles.alertAsterisk}>*</div>
                        <div style={styles.alertContent}>
                            <b>Manual</b> not available. Prior draw was over funded.
                        </div>
                    </div>
                )
            }
            {
                // Late stage draw alert
                lateStageDrawThreshold() && (
                    <div style={styles.alertSection}>
                        <div style={styles.alertAsterisk}>*</div>
                        <div style={styles.alertContent}>
                            <b>Automatic</b> recommended: late stage draw requires inspection before funding.
                        </div>
                    </div>
                )
            }
            <DialogActions>
                <div className='row drawButtonRow'>
                    <button
                        type='button'
                        className='button primary'
                        onClick={handleSubmit}
                        disabled={!canSubmit()}
                    >
                        Submit Request
                    </button>
                </div>
            </DialogActions>
        </>
    );

    return (
        <>
            <Dialog
                className='drawRequestModal'
                open={open}
                onClose={handleClose}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                style={{ zIndex: 2100 }}
            >
                <div
                    className='page-header drawHeader'
                    role='banner'
                >
                    <h2 style={{ borderBottom: 'none', transform: 'none' }}>Draw Request</h2>
                    <h2
                        style={{
                            cursor: 'pointer',
                            borderBottom: 'none',
                            transform: 'none',
                        }}
                    >
                        <CloseIcon onClick={handleClose} />
                    </h2>
                </div>
                {isSubmitted ? submittedDialog : drawSelectorDialog}
            </Dialog>
            <EsignModal
                agreementHtml={automaticAggreementString}
                isOpen={isEsignOpen}
                signatureText={borrowerName}
                cancelCallback={onRejectAgreement}
                confirmCallback={(font) => onConfirmAgreement(font)}
                ipAddress={ipAddress}
                rejectLabel='I DO NOT ACCEPT THIS AGREEMENT'
                acceptLabel='I ACCEPT THIS AGREEMENT'
            />
        </>
    );
};

const automaticAggreementString = ReactDOMServer.renderToStaticMarkup(
    <div style={{ fontSize: 20 }}>
        <p style={{ fontSize: 24, fontWeight: 600 }}>I hereby certify the following:</p>
        <ol>
            <li style={{ paddingBottom: 25 }}>I agree to draw funds.</li>
            <li style={{ paddingBottom: 25 }}>
                By selecting an Automatic draw I understand and agree that I may receive a different amount of funding than I
                expect.
            </li>
            <li style={{ paddingBottom: 25 }}>
                The amount of the draw will be based on work considered complete according to an inspection ordered by the Lender
                and potentially adjusted at Lender's sole and absolute discretion according to a request that I have made and/or
                any other consideration determined to be relevant by Lender. All work has been performed in acceptable standards
                of workmanship and quality.
            </li>
            <li style={{ paddingBottom: 25 }}>
                All work has been completed in accordance with the approved plans and specs of the project.
            </li>
            <li style={{ paddingBottom: 25 }}>
                All invoices and contracts covered by previous draws requests have been paid in full and no delinquent invoices
                exist.
            </li>
            <li>
                No unsatisfied mechanic's, workman's, or other similar liens have been placed on the property, and none are
                reasonably expected.
            </li>
        </ol>
    </div>
);
